// The meeting calendar: whose calendar BossDrop's OWN meetings are booked
// from (settings.meetingCalendarEmail, an address, or null for the workspace
// owner). A meeting someone asked for is booked from that person's own
// calendar when they connected one (the server decides that per request);
// the fallback is always the owner.
//
// Data: GET /api/calendar/organizers (fetchMeetingOrganizers), the only place
// the hub reads who has a calendar connected:
//   { ok, owner, chosen: string|null, effective: string,
//     chosenNeedsMeetAccess?: true (the choice is passed over only because
//       meeting notes are on and its Google calendar cannot read Meet
//       transcripts),
//     people: [{ email, name, role: "owner"|"admin"|"manager"|"member"|"roster",
//                google: bool, microsoft: bool, isOwner }] }
// No em dashes in anything here that reaches the screen.

export const MEETING_ORGANIZERS_ROUTE = "/api/calendar/organizers";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROLES = ["owner", "admin", "manager", "member", "roster"];
const ROLE_RANK = { owner: 0, admin: 1, manager: 2, member: 3, roster: 4 };

const lower = (v) => String(v ?? "").trim().toLowerCase();

/** settings.meetingCalendarEmail as stored: a lower-case address, or null. */
export function meetingCalendarShape(value) {
  const e = lower(value);
  return EMAIL_RE.test(e) ? e : null;
}

/**
 * GET /api/calendar/organizers, normalized (normalizeMeetingOrganizers), or
 * null when it cannot be read. `fetchImpl` is injectable for tests.
 */
export async function fetchMeetingOrganizers({ apiBase = "", fetchImpl } = {}) {
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return null;
  try {
    const base = String(apiBase || "").replace(/\/+$/, "");
    const r = await doFetch(`${base}${MEETING_ORGANIZERS_ROUTE}`, {
      credentials: "include",
      cache: "no-store",
    });
    if (!r || !r.ok) return null;
    return normalizeMeetingOrganizers(await r.json());
  } catch {
    return null;
  }
}

/**
 * { owner, chosen, effective, people: [{ email, name, role, google,
 *   microsoft, isOwner, hasCalendar }] } with addresses lower-cased, or null
 * when the payload is not that shape.
 */
export function normalizeMeetingOrganizers(payload) {
  if (!payload || payload.ok === false || !Array.isArray(payload.people)) return null;
  const owner = meetingCalendarShape(payload.owner);
  const people = [];
  for (const raw of payload.people) {
    const email = meetingCalendarShape(raw?.email);
    if (!email || people.some((p) => p.email === email)) continue;
    const isOwner = raw.isOwner === true || (!!owner && email === owner);
    const google = raw.google === true;
    const microsoft = raw.microsoft === true;
    people.push({
      email,
      name: String(raw.name || "").trim(),
      role: isOwner ? "owner" : ROLES.includes(raw.role) ? raw.role : "member",
      google,
      microsoft,
      isOwner,
      hasCalendar: google || microsoft,
    });
  }
  return {
    owner: owner || people.find((p) => p.isOwner)?.email || null,
    chosen: meetingCalendarShape(payload.chosen),
    effective: meetingCalendarShape(payload.effective),
    ...(payload.chosenNeedsMeetAccess === true ? { chosenNeedsMeetAccess: true } : {}),
    people,
  };
}

/** "Dana Whitfield", or the address when there is no name. */
export function personName(person) {
  return person ? person.name || person.email : "";
}

/** "Dana Whitfield's", "the owner's". */
export function possessive(name) {
  return name ? `${name}'s` : "the owner's";
}

function ownerOf(organizers) {
  const people = organizers?.people || [];
  return (
    people.find((p) => p.isOwner) ||
    (organizers?.owner
      ? {
          email: organizers.owner,
          name: "",
          role: "owner",
          google: false,
          microsoft: false,
          isOwner: true,
          hasCalendar: false,
        }
      : null)
  );
}

/**
 * The person BossDrop's own meetings are booked from, for the saved choice:
 * the saved person when they are in the workspace and have a calendar, else
 * the owner. The server's own `effective` wins when it answered for this same
 * choice (right after a save it may still describe the previous one).
 */
export function effectiveMeetingPerson(organizers, saved) {
  if (!organizers) return null;
  const want = meetingCalendarShape(saved);
  const byEmail = (email) => organizers.people.find((p) => p.email === email) || null;
  if (organizers.effective && organizers.chosen === want) {
    const fromServer = byEmail(organizers.effective);
    if (fromServer) return fromServer;
  }
  const picked = want ? byEmail(want) : null;
  if (picked && picked.hasCalendar) return picked;
  return ownerOf(organizers);
}

const providerIcons = (p) => [p.google ? "google" : null, p.microsoft ? "outlook" : null].filter(Boolean);

/**
 * The "Meeting calendar" row (components/ToolChoiceField.js shape):
 *   { mode, options, value, effective, tool, text, notice }
 * Options: "Workspace owner (<email>)" (null) first, then everyone with a
 * calendar (admins and managers first), then admins and managers without one,
 * greyed with where to connect it. Members and roster people without a
 * calendar are left out, so a big roster does not turn into a wall of
 * greyed rows. `status` is "loading" | "ready" | "unavailable".
 */
export function meetingCalendarRow({ organizers, saved, status = "ready" } = {}) {
  const base = { options: [], value: "", effective: null, tool: null, notice: null };
  if (status === "loading" && !organizers) {
    return { ...base, mode: "none", text: "Checking who has a calendar connected…" };
  }
  if (!organizers) {
    return { ...base, mode: "none", text: "Couldn't load who has a calendar connected." };
  }
  const want = meetingCalendarShape(saved);
  const owner = ownerOf(organizers);
  const others = organizers.people.filter((p) => !p.isOwner);
  const rank = (p) => (p.role === "admin" || p.role === "manager" ? 0 : 1);
  const byRankThenName = (a, b) =>
    rank(a) - rank(b) ||
    (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9) ||
    personName(a).localeCompare(personName(b));
  const withCalendar = others.filter((p) => p.hasCalendar).sort(byRankThenName);
  const leadsWithout = others
    .filter((p) => !p.hasCalendar && (p.role === "admin" || p.role === "manager"))
    .sort(byRankThenName);
  const effective = effectiveMeetingPerson(organizers, want);

  const ownerOption = {
    value: "",
    label: `Workspace owner (${owner ? owner.email : "not known"})`,
    icons: owner ? providerIcons(owner) : [],
  };
  const options = [
    ownerOption,
    ...withCalendar.map((p) => ({
      value: p.email,
      label: p.name ? `${p.name} (${p.email})` : p.email,
      icons: providerIcons(p),
    })),
    ...leadsWithout.map((p) => ({
      value: p.email,
      label: `${personName(p)}: connect a calendar on their card in Team directory`,
      disabled: true,
      reason: `Connect a calendar on ${possessive(personName(p))} card in Team directory`,
    })),
  ];

  // The saved person: still here but without a calendar gets one sentence;
  // someone no longer in the workspace is ignored without comment.
  const savedPerson = want ? others.find((p) => p.email === want) || null : null;
  let notice = null;
  if (savedPerson && !savedPerson.hasCalendar) {
    notice = effective?.hasCalendar
      ? `${personName(savedPerson)} has no calendar connected, so BossDrop's own meetings use ${possessive(
          personName(effective),
        )} calendar. Connect one on ${possessive(personName(savedPerson))} card in Team directory.`
      : `${personName(savedPerson)} has no calendar connected, and neither does the workspace owner, so BossDrop can't book its own meetings yet.`;
  } else if (savedPerson && organizers.chosenNeedsMeetAccess && organizers.chosen === want) {
    // Meeting notes are on and the saved person's Google calendar cannot read
    // Meet transcripts, so the server books from the owner's instead.
    notice = `${personName(savedPerson)} hasn't allowed Google Meet transcripts, so while meeting notes are on, BossDrop's own meetings use ${possessive(
      personName(effective),
    )} calendar. ${personName(savedPerson)} can allow it in Meeting notes.`;
  }
  const value =
    effective && !effective.isOwner && withCalendar.some((p) => p.email === effective.email)
      ? effective.email
      : "";

  const anyCalendar = !!owner?.hasCalendar || withCalendar.length > 0;
  if (!anyCalendar && !leadsWithout.length) {
    return {
      ...base,
      mode: "none",
      effective,
      text: `Connect a calendar on ${possessive(personName(owner))} card in Team directory.`,
      notice: null,
    };
  }
  if (options.length === 1) {
    return {
      ...base,
      mode: "single",
      options,
      value: "",
      effective,
      tool: owner && owner.google ? "google" : owner && owner.microsoft ? "outlook" : null,
      text: owner?.hasCalendar
        ? `Uses the workspace owner's calendar (${owner.email})`
        : `Connect a calendar on ${possessive(personName(owner))} card in Team directory.`,
      notice,
    };
  }
  return { ...base, mode: "select", options, value, effective, notice };
}

/** The effective person's calendars in useCalendarConnections()'s shape. */
export function meetingPersonCalendars(person) {
  if (!person) return { status: "unknown", google: false, outlook: false };
  return { status: "ready", google: !!person.google, outlook: !!person.microsoft };
}
